import { useState, useCallback, useEffect } from "react";
import api from "@/auth/api";
import { alertError, alertSuccess } from "@/components/Alert";
import { fetchPanelUser } from "@/slices/panelUserSlice";
import { useDispatch, useSelector } from "react-redux";
import SampleTable from "./SampleTable";
import { AsyncPaginate } from "react-select-async-paginate";
import { components } from "react-select";

// ... (Constants and Utilities remain the same)

// ─── Constants ────────────────────────────────────────────────────────────────

const FIELDS = [
  { label: "Profile Complete", value: "profile_complete", type: "boolean" },
  { label: "Profile Pic", value: "profile_pic", type: "boolean" },
  { label: "Add Phone No", value: "Phone_No", type: "boolean" },
  { label: "Is Birthday", value: "is_birthday", type: "boolean" },
  { label: "Phone No Verified", value: "is_Phone_No_verified", type: "boolean" },
];

const OPERATORS = [
  { label: "Equals", value: "eq" },
  { label: "Not Equals", value: "neq" },
  { label: "Greater Than", value: "gt" },
  { label: "Less Than", value: "lt" },
  { label: "Contains", value: "contains" },
];

// ─── Utilities ────────────────────────────────────────────────────────────────
function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function createRule() {
  return { id: uid(), field: "profile_complete", operator: "eq", value: true };
}

function createGroup() {
  return { id: uid(), type: "AND", rules: [] };
}

function getFieldMeta(fieldValue) {
  return FIELDS.find((f) => f.value === fieldValue) || FIELDS[0];
}

function normalizeValue(type, raw) {
  if (type === "boolean") return raw === "true" || raw === true;
  if (type === "number") return raw === "" ? "" : Number(raw);
  return raw;
}

function stripIds(node) {
  if (node.rules) {
    return { type: node.type, rules: node.rules.map(stripIds) };
  }
  return { field: node.field, operator: node.operator, value: node.value };
}

// ─── Toggle Switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1.5 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-7 h-4 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${checked ? "bg-indigo-500" : "bg-gray-300"
          }`}
      >
        <div
          className={`absolute top-[2px] w-3 h-3 rounded-full bg-white shadow transition-all duration-200 ${checked ? "left-[14px]" : "left-[2px]"
            }`}
        />
      </div>
      <span className="text-xs text-gray-700 font-medium whitespace-nowrap">{label}</span>
    </label>
  );
}

// ─── Value Input ──────────────────────────────────────────────────────────────
function ValueInput({ rule, onChange }) {
  const meta = getFieldMeta(rule.field);
  const inputClass = "w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition";

  if (meta.type === "boolean") {
    return (
      <select
        value={String(rule.value)}
        onChange={(e) => onChange(normalizeValue("boolean", e.target.value))}
        className={inputClass}
      >
        <option value="true">True</option>
        <option value="false">False</option>
      </select>
    );
  }

  if (meta.type === "number") {
    return (
      <input
        type="number"
        value={rule.value ?? ""}
        onChange={(e) => onChange(normalizeValue("number", e.target.value))}
        className={inputClass}
      />
    );
  }

  return (
    <input
      type="text"
      value={rule.value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={inputClass}
    />
  );
}

// ─── Rule Row ─────────────────────────────────────────────────────────────────
function RuleRow({ rule, onUpdate, onRemove }) {
  const selectClass = "w-full px-2 py-1 text-xs rounded border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 transition";

  return (
    <div className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
      <div className="flex-[1.5]">
        <select
          value={rule.field}
          onChange={(e) => {
            const meta = getFieldMeta(e.target.value);
            onUpdate({
              ...rule,
              field: e.target.value,
              value: meta.type === "boolean" ? true : meta.type === "number" ? 0 : "",
            });
          }}
          className={selectClass}
        >
          {FIELDS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      <div className="flex-1">
        <select
          value={rule.operator}
          onChange={(e) => onUpdate({ ...rule, operator: e.target.value })}
          className={selectClass}
        >
          {OPERATORS.map((op) => (
            <option key={op.value} value={op.value}>{op.label}</option>
          ))}
        </select>
      </div>

      <div className="flex-[1.2]">
        <ValueInput rule={rule} onChange={(v) => onUpdate({ ...rule, value: v })} />
      </div>

      <button
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
        title="Remove Rule"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
}

// ─── Group Block (recursive) ──────────────────────────────────────────────────
function GroupBlock({ group, isRoot, onUpdate, onRemove }) {
  function updateChild(id, newNode) {
    const newRules = group.rules.map((r) => (r.id === id ? newNode : r));
    onUpdate({ ...group, rules: newRules });
  }

  function removeChild(id) {
    onUpdate({ ...group, rules: group.rules.filter((r) => r.id !== id) });
  }

  function addRule() {
    onUpdate({ ...group, rules: [...group.rules, createRule()] });
  }

  function addGroup() {
    onUpdate({ ...group, rules: [...group.rules, createGroup()] });
  }

  return (
    <div className={`relative pl-4 border-l-2 ${group.type === 'OR' ? 'border-amber-100' : 'border-indigo-100'} mb-2`}>
      <div className="flex items-center gap-3 mb-2">
        <select
          value={group.type}
          onChange={(e) => onUpdate({ ...group, type: e.target.value })}
          className={`text-[10px] font-black rounded px-2 py-0.5 focus:outline-none transition-all shadow-sm ${
            group.type === 'OR' ? 'bg-amber-500 text-white' : 'bg-indigo-500 text-white'
          }`}
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
        
        <div className="flex gap-2">
          <button onClick={addRule} className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 uppercase tracking-tight">+ Add Rule</button>
          <button onClick={addGroup} className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 uppercase tracking-tight">+ Add Group</button>
          {!isRoot && (
            <button onClick={onRemove} className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-tight">Remove</button>
          )}
        </div>
      </div>

      <div className="flex flex-col">
        {group.rules.length === 0 && (
          <p className="text-gray-300 text-[10px] italic py-1">No rules in this group</p>
        )}
        {group.rules.map((r) =>
          r.rules !== undefined ? (
            <GroupBlock
              key={r.id}
              group={r}
              isRoot={false}
              onUpdate={(newG) => updateChild(r.id, newG)}
              onRemove={() => removeChild(r.id)}
            />
          ) : (
            <RuleRow
              key={r.id}
              rule={r}
              onUpdate={(newR) => updateChild(r.id, newR)}
              onRemove={() => removeChild(r.id)}
            />
          )
        )}
      </div>
    </div>
  );
}


const CustomMenuList = (props) => {
  const {
    children,
    selectProps: { options, value, onChange },
  } = props;

  const allSelected =
    options.length > 0 &&
    value?.length === options.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options);
    }
  };

  return (
    <components.MenuList {...props}>
      <div
        className="px-3 py-2 border-b border-gray-200 bg-gray-50"
      >
        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
          />
          Select All
        </label>
      </div>

      {children}
    </components.MenuList>
  );
};


// ─── Main App ─────────────────────────────────────────────────────────────────
export default function BannerSample() {
  const [pii, setPii] = useState([]);
  const [template, setTemplate] = useState("");
  const [isCheckCond, setIsCheckCond] = useState(true);
  const [isCheckTime, setIsCheckTime] = useState(false);
  const [isCheckDatetime, setIsCheckDatetime] = useState(false);
  const [status, setStatus] = useState(true);
  const [timeMinutes, setTimeMinutes] = useState(1440);
  const [startDt, setStartDt] = useState("");
  const [endDt, setEndDt] = useState("");
  const [root, setRoot] = useState({ id: uid(), type: "AND", rules: [] });
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bannerList, setBannerList] = useState([]);
  const [sampleList, setSampleList] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  

  const dispatch = useDispatch();
  const { panelUserList, status: panelUserStatus } = useSelector((state) => state.panelUser);

  const fetchBanners = async () => {
    try {
      const url = '/surveyHistory/panel/notification/banner/template';
      const resp = await api.get(url);
      setBannerList(resp?.data?.result);
    } catch (err) { }
  }

  const fetchSampleList = useCallback(async (page = 1, pageSize = pagination.pageSize) => {
    try {
      const url = `/surveyHistory/panel/notification/banner/user?page=${page}&page_size=${pageSize}`;
      const resp = await api.get(url);
      setSampleList(resp?.data?.result || []);
      setPagination({
        page: page,
        pageSize: pageSize,
        total: resp?.data?.pagination?.count || 0
      });
    } catch (err) {
      console.log(err?.response?.data?.message || err.message)
    }
  }, [pagination.pageSize]);

  useEffect(() => {
       dispatch(fetchPanelUser({ fetchAll: true }))
       fetchBanners()
  }, [dispatch])

  useEffect(() => {
    fetchSampleList(1, pagination.pageSize);
  }, [pagination.pageSize, fetchSampleList]);

  const getPayload = useCallback(
    () => ({
      // pii,
      pii: pii.map((item) => item.value),
      template: Number(template),
      status,
      is_check_cond: isCheckCond,
      is_check_time: isCheckTime,
      is_check_datetime: isCheckDatetime,
      time: isCheckTime ? Number(timeMinutes) : null,
      startdateTime: startDt || null,
      enddateTime: isCheckDatetime ? endDt || null : null,
      condition: isCheckCond ? stripIds(root) : null,
    }),
    [pii, template, status, isCheckCond, isCheckTime, isCheckDatetime, timeMinutes, startDt, endDt, root]
  );

  const payload = getPayload();
  const payloadStr = JSON.stringify(payload, null, 2);

  async function handleCopy() {
    await navigator.clipboard.writeText(payloadStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }


  async function handleSubmit() {
    if (!payload.pii) { alertError("PII is required"); return; }
    if (!payload.template) { alertError("Template ID is required"); return; }
    if (payload.is_check_cond && (!payload.condition || payload.condition.rules.length === 0)) {
      alertError("Add at least one condition rule");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/surveyHistory/panel/notification/banner/user", payload);
      alertSuccess("Banner Sampled Successfully");
      fetchSampleList();
    } catch (err) {
      alertError("Error: " + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  }

  function loadSample1() {
    setPii("Upload Profile Picture");
    setTemplate("101");
    setIsCheckCond(true);
    setIsCheckTime(true);
    setIsCheckDatetime(false);
    setTimeMinutes(1440);
    setRoot({
      id: uid(),
      type: "AND",
      rules: [{ id: uid(), field: "profile_pic", operator: "eq", value: false }],
    });
  }

  function loadSample2() {
    setPii("Complete Profile");
    setTemplate("202");
    setIsCheckCond(true);
    setIsCheckTime(false);
    setIsCheckDatetime(false);
    setRoot({
      id: uid(),
      type: "OR",
      rules: [
        {
          id: uid(),
          type: "AND",
          rules: [
            { id: uid(), field: "profile_complete", operator: "eq", value: true },
            { id: uid(), field: "Phone_No", operator: "eq", value: true },
          ],
        },
        { id: uid(), field: "profile_pic", operator: "eq", value: true },
      ],
    });
  }


//   const loadPanelUsers = async (
//   search,
//   loadedOptions,
//   { page }
// ) => {
//   try {
//     const response = await dispatch(
//       fetchPanelUser({
//         page,
//         search,
//         fetchAll: false,
//       })
//     ).unwrap();

//     const users = response?.data?.result || [];

//     return {
//       options: users.map((item) => ({
//         value: item.pii,
//         label: item.email,
//       })),

//       hasMore:
//         users.length >= 10,

//       additional: {
//         page: page + 1,
//       },
//     };
//   } catch (error) {
//     return {
//       options: [],
//       hasMore: false,
//       additional: {
//         page: 1,
//       },
//     };
//   }
// };


const loadPanelUsers = async (
  search,
  loadedOptions,
  { page }
) => {
  try {
    const response = await dispatch(
      fetchPanelUser({
        page,
        search,
        fetchAll: false,
      })
    ).unwrap();

    const users = response?.data?.result || [];

    // only active + verified users
    const filteredUsers = users.filter(
      (item) => item.is_active && item.is_emailVerified
    );

    return {
      options: filteredUsers.map((item) => ({
        value: item.pii,
        label: item.email,
      })),

      hasMore: filteredUsers.length >= 10,

      additional: {
        page: page + 1,
      },
    };
  } catch (error) {
    return {
      options: [],
      hasMore: false,
      additional: {
        page: 1,
      },
    };
  }
};

  const inputCls =
    "w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition";

  return (
    <div className="min-h-screen bg-gray-50 font-sans w-full">
      <div className="px-4 py-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base font-semibold text-gray-800">Banner Settings</h2>
            <button className="px-4 py-1.5 bg-indigo-500 text-white text-sm font-medium rounded-md hover:bg-indigo-600 transition-colors" onClick={handleSubmit}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>

          <div className="flex flex-col gap-4">
            {/* Toggles Row */}
            <div className="flex flex-wrap items-center gap-2">
              <Toggle checked={isCheckCond} onChange={setIsCheckCond} label="Condition Check" />
              <Toggle checked={isCheckTime} onChange={setIsCheckTime} label="Time Check" />
              <Toggle checked={isCheckDatetime} onChange={setIsCheckDatetime} label="Datetime Check" />
              <Toggle checked={status} onChange={setStatus} label="Status" />
            </div>

            {/* Inputs Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">PII</label>
                 {/* <select value={pii} onChange={(e) => setPii(e.target.value)} className={inputCls}>
                  <option value="">Select PII</option>
                  {panelUserList?.result?.map((item) => (
                    <option key={item.id} value={item.pii}>
                      {item.email}
                    </option>
                  ))}
                </select> */}

               {/* <AsyncPaginate
                value={
                  pii
                    ? {
                        value: pii,
                        label: pii,
                      }
                    : null
                }
                loadOptions={loadPanelUsers}
                onChange={(selected) => setPii(selected?.value || "")}
                additional={{
                  page: 1,
                }}
                debounceTimeout={500}
                placeholder="Search User..."
                isClearable
              /> */}

              <AsyncPaginate
                isMulti
                value={pii}
                loadOptions={loadPanelUsers}
                onChange={(selected) => setPii(selected || [])}
                additional={{
                  page: 1,
                }}
                debounceTimeout={500}
                placeholder="Search User..."
                isClearable
                closeMenuOnSelect={false}
                components={{
                  MenuList: CustomMenuList,
                }} />
              </div>


              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Template ID</label>
                <select value={template} onChange={(e) => setTemplate(e.target.value)} className={inputCls} style={{height : "36px"}}>
                  <option value="">Select Template</option>
                  {bannerList?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              </div>

              {isCheckTime && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Time (mins)</label>
                  <input type="number" min={1} value={timeMinutes} onChange={(e) => setTimeMinutes(e.target.value)} className={inputCls} />
                </div>
              )}
              {(isCheckTime || isCheckDatetime) && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Start Datetime</label>
                  <input type="datetime-local" value={startDt} onChange={(e) => setStartDt(e.target.value)} className={inputCls} />
                </div>
              )}
              {isCheckDatetime && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">End Datetime</label>
                  <input type="datetime-local" value={endDt} onChange={(e) => setEndDt(e.target.value)} className={inputCls} />
                </div>
              )}
            </div>

            {/* Condition Builder */}
            {isCheckCond && (
              <div className="mt-2 border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-800">Condition Builder</h2>
                  <div className="flex gap-1.5">
                    <button onClick={loadSample1} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors">Sample 1</button>
                    <button onClick={loadSample2} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-200 transition-colors">Sample 2</button>
                  </div>
                </div>

                <GroupBlock
                  group={root}
                  isRoot={true}
                  onUpdate={setRoot}
                  onRemove={() => { }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <SampleTable 
        sampleList={sampleList} 
        fetchSampleList={fetchSampleList} 
        pagination={pagination}
        setPagination={setPagination}
      />
    </div>
  );
}
